import { api } from './api'

export interface YearWiseFee {
  yearNumber: number
  feeAmount: number
}

export interface CourseGroup {
  id: string
  name: string
  code: string
  durationYears: number
  totalFee: number
  description?: string
  isActive: boolean
  formattedFee: string
  studentCount: number
  yearWiseFees?: YearWiseFee[]
  college: {
    id: string
    name: string
    code: string
  }
  createdAt: string
  updatedAt: string
}

export interface CreateCourseGroupData {
  name: string
  code: string
  durationYears: number
  totalFee: number
  description?: string
}

export interface CreateCourseGroupWithYearwiseFeesData {
  name: string
  code: string
  durationYears: number
  description?: string
  yearWiseFees: YearWiseFee[]
}

export interface UpdateCourseGroupData {
  name?: string
  code?: string
  description?: string
}

export interface UpdateCourseGroupWithYearwiseFeesData {
  name?: string
  code?: string
  description?: string
  yearWiseFees?: YearWiseFee[]
}

export interface CourseGroupQuery {
  page?: number
  limit?: number
  search?: string
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
}

export interface CourseGroupResponse {
  data: CourseGroup[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

export const courseGroupService = {
  // Get all course groups with pagination
  async getAllCourseGroups(params: CourseGroupQuery = {}): Promise<CourseGroupResponse> {
    const searchParams = new URLSearchParams()
    
    if (params.page) searchParams.append('page', params.page.toString())
    if (params.limit) searchParams.append('limit', params.limit.toString())
    if (params.search) searchParams.append('search', params.search)
    if (params.sortBy) searchParams.append('sortBy', params.sortBy)
    if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder)

    const response = await api.get(`/course-groups?${searchParams.toString()}`)
    return response.data
  },

  // Get course group by ID
  async getCourseGroupById(id: string): Promise<CourseGroup> {
    const response = await api.get(`/course-groups/${id}`)
    return response.data.data
  },

  // Create new course group
  async createCourseGroup(data: CreateCourseGroupData): Promise<CourseGroup> {
    const response = await api.post('/course-groups', data)
    return response.data.data
  },

  // Create new course group with year-wise fees
  async createCourseGroupWithYearwiseFees(data: CreateCourseGroupWithYearwiseFeesData): Promise<CourseGroup> {
    const response = await api.post('/course-groups/with-yearwise-fees', data)
    return response.data.data
  },

  // Update course group (name/code only)
  async updateCourseGroup(id: string, data: UpdateCourseGroupData): Promise<CourseGroup> {
    const response = await api.put(`/course-groups/${id}`, data)
    return response.data.data
  },

  // Update course group with year-wise fees
  async updateCourseGroupWithYearwiseFees(id: string, data: UpdateCourseGroupWithYearwiseFeesData): Promise<CourseGroup> {
    const response = await api.put(`/course-groups/${id}/with-yearwise-fees`, data)
    return response.data.data
  },

  // Delete course group
  async deleteCourseGroup(id: string): Promise<void> {
    await api.delete(`/course-groups/${id}`)
  }
}
